<template>
    <div>
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border"></div>
        </div>
        <div v-else>
            <!-- Customer Details Section -->
            <div class="row">
                <div class="col-md-4 col-sm-4 col-lg-4">
                    <div class="container pt-2">
                        <a class="text-primary underline-primary" @click="orderGoBack">
                            <i class="fas fa-angle-left fa-1x"></i> &nbsp;Go Back
                        </a>
                        <hr />
                        <div class="text-left">
                            <h4 class="py-3 pl-2">Customer Details</h4>
                            <div v-if="hasCustomer">
                                <h6 v-if="customerFullName">
                                    <i class="bi bi-person"></i> {{ customerFullName }}
                                    <hr class="my-2" />
                                </h6>
                                <h6 v-if="customerPhone">
                                    <i class="bi bi-phone"></i> {{ customerPhone }}
                                    <hr class="my-2" />
                                </h6>
                                <h6 v-if="customerEmail">
                                    <i class="bi bi-envelope"></i> {{ customerEmail }}
                                    <hr class="my-2" />
                                </h6>
                                <p>
                                    <i class="bi bi-pin"></i>{{ customerAddress }}
                                </p>
                            </div>
                            <div v-else>
                                <h6><i class="bi bi-person"></i> Default Customer</h6>
                            </div>
                        </div>

                        <!-- Loyalty balance (read-only here — redemption happens in the
                             payment modal, see choosePaymentMethodModal below) -->
                        <div v-if="loyaltyEnabled && hasCustomer" class="text-left mt-3">
                            <hr />
                            <h6 class="mb-0"><i class="bi bi-star"></i> Loyalty points
                                <span v-if="loyaltyLoading" class="spinner-border spinner-border-sm ml-1"></span>
                                <span v-else-if="loyaltyBalance !== null" class="text-primary font-weight-bold">{{ loyaltyBalance }} pts</span>
                            </h6>
                            <small v-if="orderDetails.loyalty_discount > 0" class="text-primary d-block mt-1">
                                {{ appliedLoyaltyPoints }} pts redeemed for {{ convertCurrencyToLocal(orderDetails.loyalty_discount) }} off
                            </small>
                        </div>
                    </div>
                </div>

                <!-- Payment Section -->
                <div class="col-md-8 col-sm-8 col-lg-8">
                    <div class="container pt-3">
                        <div class="text-center">
                            <h2>Select Payment Method</h2>
                        </div>
                        <hr/>

                        <!-- Payment Amount Display -->
                        <div class="row">
                            <div class="col-6">
                                <div class="float-left">
                                    <h5 class="pt-3">Pay</h5>
                                </div>
                            </div>
                            <div class="col-6">
                                <div class="float-right">
                                    <h5 class="pt-3">{{ displayAmount }}</h5>
                                    <hr/>
                                </div>
                            </div>
                        </div>

                        <!-- Action Buttons with KDS -->
                        <div v-if="hasKDS" class="d-flex">
                            <a href="/orders" class="btn rounded btn-secondary flex-grow-1 mx-1">
                                Hold Order
                            </a>
                            <a v-b-modal="'create-kitchen-note-modal'"
                               class="btn rounded btn-outline-primary flex-grow-1 mx-1">
                                Send To Kitchen
                            </a>
                            <a v-if="canProcessPayments"
                               @click="choosePaymentMethodModalShow"
                               class="btn rounded flex-grow-1 btn-primary">
                                Process Payments
                            </a>
                            <a v-else
                               :href="printInvoiceUrl"
                               class="btn rounded flex-grow-1 btn-primary">
                                Print Customer Receipt
                            </a>
                        </div>

                        <!-- Action Buttons without KDS -->
                        <div v-else class="d-flex">
                            <a v-if="canDeleteOrders"
                               @click="discardOrder(orderDetails)"
                               class="btn rounded btn-outline-secondary flex-grow-1">
                                Cancel Order
                            </a>
                            <a href="/orders" class="btn rounded btn-secondary flex-grow-1 mx-1">
                                Hold Order
                            </a>
                            <a @click="choosePaymentMethodModalShow"
                               class="btn rounded flex-grow-1 btn-primary">
                                Process Payments
                            </a>
                        </div>

                        <p>
                            <small v-if="hasDisabledPayments" class="text-danger">
                                You can enable payments methods in settings
                            </small>
                        </p>

                        <!-- Order Items Table -->
                        <div class="container d-inline">
                            <table class="table">
                                <thead>
                                    <tr>
                                        <td>Item</td>
                                        <td>Qty</td>
                                        <td>Amount</td>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="orderItem in validOrderItems">
                                        <tr :key="orderItem.id || orderItem.product_id">
                                            <td>
                                                <template v-if="orderItem.product_id">
                                                    {{ getProductName(orderItem) }}
                                                </template>
                                                <template v-else>
                                                    {{ getGroupTitle(orderItem) }}
                                                    <hr class="m-0" />
                                                    <ul class="unstyled">
                                                        <li v-for="item in getGroupedItems(orderItem)"
                                                            :key="item.id">
                                                            {{ getItemDisplay(item) }}
                                                        </li>
                                                    </ul>
                                                </template>
                                            </td>
                                            <td>{{ orderItem.qty }}</td>
                                            <td>{{ calculateItemAmount(orderItem) }}</td>
                                        </tr>
                                    </template>

                                    <!-- Subtotal -->
                                    <tr>
                                        <td><h6 class="font-weight-bolder">Sub total</h6></td>
                                        <td></td>
                                        <td><h6 class="font-weight-bolder">{{ formattedSubTotal }}</h6></td>
                                    </tr>

                                    <!-- Discount -->
                                    <tr v-if="hasDiscount">
                                        <td><h6 class="font-weight-bolder">Discount</h6></td>
                                        <td></td>
                                        <td><h6 class="font-weight-bolder">{{ formattedDiscount }}</h6></td>
                                    </tr>

                                    <!-- Balance -->
                                    <tr v-if="isPartialPayment">
                                        <td><h6 class="font-weight-bolder">Balance</h6></td>
                                        <td></td>
                                        <td><h6 class="font-weight-bolder">{{ formattedBalance }}</h6></td>
                                    </tr>

                                    <!-- Grand Total -->
                                    <tr>
                                        <td><h4 class="font-weight-bolder">Grand Total</h4></td>
                                        <td></td>
                                        <td><h4 class="font-weight-bolder">{{ formattedGrandTotal }}</h4></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <hr/>
                    </div>
                </div>
            </div>


            <b-modal ref="paidAmountmodal" hide-footer hide-title>
                <div class="container py-3">
                    <!-- Cash Amount -->
                    <div class="row align-items-center mb-3">
                        <div class="col-md-6">
                            <h5 class="text-primary mb-0">Cash Amount</h5>
                            <small class="text-muted">Amount of cash received from the customer</small>
                        </div>
                        <div class="col-md-6">
                            <input class="form-control" v-model="paidAmount" type="number" placeholder="Enter cash received" />
                            <small class="text-muted">Leave blank to confirm without recording change</small>
                            <div class="d-flex flex-wrap mt-1" style="gap:0.25rem;">
                                <button v-for="d in [50, 100, 200, 500, 1000]" :key="d"
                                    class="badge badge-secondary"
                                    style="border:none;cursor:pointer;font-size:0.72rem;padding:3px 6px;"
                                    @click="paidAmount = d">
                                    {{ d }}
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- Order Amount -->
                    <div class="row align-items-center mb-3">
                        <div class="col-md-6">
                            <h5 class="text-primary mb-0">Order Amount</h5>
                            <small class="text-muted">Total amount of the order</small>
                        </div>
                        <div class="col-md-6">
                            <h4 class="font-weight-bold mb-0">{{ convertCurrencyToLocal(splitPaymentAmount) }}</h4>
                        </div>
                    </div>
                    <!-- Change -->
                    <div class="row align-items-center mb-4">
                        <div class="col-md-6">
                            <h5 class="text-primary mb-0">Change</h5>
                            <small class="text-muted">Amount to return to the customer</small>
                        </div>
                        <div class="col-md-6">
                            <h4 v-if="changeToCustomer > 0" class="font-weight-bold text-success mb-0">
                                {{ convertCurrencyToLocal(changeToCustomer) }}
                            </h4>
                            <span v-else class="text-muted">No change to return</span>
                        </div>
                    </div>
                    <!-- Confirm -->
                    <div class="text-right">
                        <small v-if="cashAmountError" class="text-danger d-block mb-2">{{ cashAmountError }}</small>
                        <button class="btn btn-primary btn-block" @click="cashCheckout()">Confirm Payment</button>
                    </div>
                </div>
            </b-modal>
            <!-- M-Pesa STK from the company's own Paybill/Till (shared with MiniCheckout) -->
            <mpesa-stk-modal
                ref="mpesaStk"
                :order-id="orderDetails.id"
                :amount="Number(mpesaPromptAmount || orderDetails.balance || orderDetails.total_amount || 0)"
                :default-phone="customerPhone"
                @paid="onStkPaid"
                @skip="onStkSkip"
                @cancel="choosePaymentMethodModalShow"
            />
            <b-modal ref="customer-dukakit-phone-request" hide-footer hide-title>
                <div class="d-block text-center">
                    <h2>Enter customer number that will be used to pay</h2>
                    <small>Order amount {{ convertCurrencyToLocal(orderDetails.total_amount) }}</small>
                    <div class="container py-3">
                        <form @submit.prevent="sendDukakitStkPush">
                            <vue-tel-input v-on:validate="validateDukakitPhoneNumber" v-model="dukakitPhoneNumber"></vue-tel-input>
                            <small v-if="dukakitPhoneValidMessage" class="text-danger d-block mt-2">{{ dukakitPhoneValidMessage }}</small>
                            <small v-if="dukakitPushError" class="text-danger d-block mt-2">{{ dukakitPushError }}</small>
                            <button type="submit" class="btn btn-primary my-2 btn-block" :disabled="dukakitPushSending || !!dukakitPhoneValidMessage">
                                {{ dukakitPushSending ? 'Sending...' : `Send ${$platformBrand.name} Payment Request` }}
                            </button>
                        </form>
                    </div>
                </div>
            </b-modal>
            <b-modal id="modal-5" ref="choosePaymentMethodModal" hide-footer>
                <h5 class="text-center pt-2">Balance - {{ convertCurrencyToLocal(orderDetails.balance) }}</h5>

                <!-- Loyalty redemption -->
                <div v-if="loyaltyEnabled && hasCustomer" class="text-center pb-2">
                    <div v-if="orderDetails.loyalty_discount > 0">
                        <small class="text-primary">{{ appliedLoyaltyPoints }} pts redeemed for {{ convertCurrencyToLocal(orderDetails.loyalty_discount) }} off</small>
                        <a href="#" class="small ml-2" @click.prevent="undoLoyaltyRedemption">Undo</a>
                    </div>
                    <div v-else-if="loyaltyBalance > 0">
                        <a v-if="!showLoyaltyRedeemInput" href="#" class="small" @click.prevent="showLoyaltyRedeemInput = true">
                            Customer's points: <strong>{{ loyaltyBalance }}</strong> &middot; <span class="text-primary">Redeem</span>
                        </a>
                        <div v-else-if="!loyaltyOtpSentTo" class="d-flex justify-content-center align-items-center" style="gap:6px;">
                            <input type="number" min="1" :max="loyaltyBalance" class="form-control form-control-sm" style="width:100px;" v-model.number="redeemPointsInput" placeholder="Points" />
                            <button class="btn btn-sm btn-primary" :disabled="redeemingLoyalty || !redeemPointsInput" @click="redeemLoyaltyPoints">
                                <span v-if="redeemingLoyalty" class="spinner-border spinner-border-sm"></span>
                                <span v-else>Redeem</span>
                            </button>
                            <a href="#" class="small text-dark" @click.prevent="showLoyaltyRedeemInput = false">Cancel</a>
                        </div>
                        <div v-else>
                            <small class="d-block mb-1">Code sent to customer's phone {{ loyaltyOtpSentTo }} for {{ redeemPointsInput }} pts</small>
                            <div class="d-flex justify-content-center align-items-center" style="gap:6px;">
                                <input type="text" inputmode="numeric" maxlength="6" class="form-control" style="width:120px;" v-model.trim="loyaltyOtpInput" placeholder="Code" @keyup.enter="confirmLoyaltyOtp" />
                                <button class="btn btn-primary" :disabled="redeemingLoyalty || !loyaltyOtpInput" @click="confirmLoyaltyOtp">
                                    <span v-if="redeemingLoyalty" class="spinner-border spinner-border-sm"></span>
                                    <span v-else>Confirm</span>
                                </button>
                                <a href="#" class="small" @click.prevent="redeemLoyaltyPoints">Resend</a>
                                <a href="#" class="small text-dark" @click.prevent="cancelLoyaltyOtp">Cancel</a>
                            </div>
                        </div>
                        <small v-if="loyaltyRedeemError" class="text-danger d-block mt-1">{{ loyaltyRedeemError }}</small>
                    </div>
                </div>

                <hr/>
                <div class="d-inline" v-if="paymentGateways.length < 0">
                    <span>Loading..</span>
                    <div class="spinner-grow spinner-grow-sm" role="status">
                        <span class="sr-only">Loading payments...</span>
                    </div>
                </div>
                <select v-if="paymentGateways" class="custom-select" v-model="splitPaymentSelect">
                    <option value="null">--Select Payment--</option>
                    <option
                        v-for="payment in paymentGateways"
                        v-if="payment.enabled && (payment.payment_method !== 'wallet' || walletEnabled)"
                        :key="payment.id"
                        :value="payment"
                    >{{ payment.payment_method === 'wallet'
                            ? ('Wallet · ' + (walletBalance !== null ? convertCurrencyToLocal(walletBalance) : '…'))
                            : (payment.payment_method + (payment.account_label ? ' · ' + payment.account_label : payment.account_number ? ' · ' + payment.account_number : ''))
                        }}</option>
                </select>
                <div v-else class="spinner-grow" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <small class="text-danger">{{ splitPaymentSelectError }}</small>

                <hr/>
                <div v-if="splitPaymentAmount !==false && splitPaymentAmount !== null">
                    <input v-model="splitPaymentAmount" class="form-control" placeholder="Enter amount" />
                    <small v-if="splitPaymentSelect && splitPaymentSelect.payment_method === 'wallet' && walletBalance !== null" class="text-muted d-block mb-1">
                        Available wallet balance: {{ convertCurrencyToLocal(walletBalance) }}
                    </small>
                    <small v-if="walletPaymentError" class="text-danger d-block mb-1">{{ walletPaymentError }}</small>
                    <p><small>To split Payment you can change amount (Applies to deposits)</small></p>
                    <div v-if="splitPaymentAmount !==false && splitPaymentAmount !=='' && splitPaymentAmount !== null && paymentGateways.length > 0 " class="py-3 text-center">
                        <a v-if="splitPaymentBtnTxt==='Process the balance'" class="btn btn-outline-secondary btn-sm mx-1" :href="checkOutDoneLink">Continue To Print Reciept</a>
                        <a :class="['btn', 'btn-primary','btn-sm', 'mx-1', 'btn-block', { 'disabled': isSubmitting }]" @click="processSplitPaymentModal()">{{ splitPaymentBtnTxt }}</a>
                    </div>
                </div>
                <div v-else class="spinner-grow" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
            </b-modal>
            <b-modal ref="process_dukakit" id="modal-center-dukakit" hide-header hide-footer>
                <div class="text-center pt-3">
                    <div v-html="dukakitStatusHolder">
                    </div>
                </div>
            </b-modal>
            <b-modal ref="validationModal" hide-footer hide-header :no-close-on-backdrop="true" :no-close-on-esc="true">
                <payment-validation-waiting
                  v-if="validationActive && orderDetails.id"
                  :expected-amount="validationAmount"
                  record-type="order"
                  :record-id="orderDetails.id"
                  :payment-method="validationPaymentMethod"
                  :payment-method-id="validationPaymentId"
                  :supports-push="validationPaymentMethod === 'dukakit'"
                  :push-sending="dukakitPushSending"
                  :push-error="dukakitPushError"
                  @validated="onValidated"
                  @excess-detected="onExcessDetected"
                  @request-push="onDukakitPushRequested"
                />
            </b-modal>
            <b-modal ref="excessWalletModal" hide-footer :no-close-on-backdrop="true" :no-close-on-esc="true" title="Excess payment">
                <div v-if="excessInfo">
                    <p>
                        SMS payment <strong>{{ excessInfo.transaction_code }}</strong> covers the order balance of
                        {{ convertCurrencyToLocal(excessInfo.order_balance) }} with
                        {{ convertCurrencyToLocal(excessInfo.excess) }} left over.
                    </p>
                    <p class="small text-muted mb-1">Load the excess onto whose wallet?</p>
                    <div class="form-group">
                        <div v-if="!showExcessCustomerSearch" class="d-flex justify-content-between align-items-center">
                            <span>{{ excessWalletCustomerLabel || 'No customer selected' }}</span>
                            <a href="#" class="small" @click.prevent="showExcessCustomerSearch = true">Change</a>
                        </div>
                        <customer-search-select
                          v-else
                          v-model="excessWalletCustomerId"
                          @customer-selected="onExcessWalletCustomerSelected"
                        />
                    </div>
                    <small v-if="excessError" class="text-danger d-block mb-2">{{ excessError }}</small>
                    <div class="text-right">
                        <button
                          class="btn btn-primary btn-sm"
                          :disabled="!excessWalletCustomerId || excessResolving"
                          @click="resolveExcessToWallet"
                        >
                            <span v-if="excessResolving"><i class="spinner-border spinner-border-sm"></i></span>
                            <span v-else>Confirm &amp; Complete Sale</span>
                        </button>
                    </div>
                </div>
            </b-modal>
            <b-modal id="new_customers_modal" ref="new_customers_modal" hide-footer static>
                <div class="Text-centre">
                    <h2 class="font-weight-bold text-dark ">Add Customer</h2>
                </div>
                <div class="container py-3">
                    <!-- Actual search box -->
                    <form @submit.prevent="newCustomerInfo">
                        <div class="form-row py-1">
                            <div class="col-6">
                                <input class="form-control border-bottom" v-model="newCustomerForm.f_name" name="f_name" type="text" placeholder="First Name" />
                                <!--<small class="text-danger">{{ errors.first('f_name') }}</small>-->
                            </div>
                            <div class="col-6">
                                <input class="form-control border-bottom" v-model="newCustomerForm.l_name" name="l_name" type="text" placeholder="Last Name" />
                                <!--<small class="text-danger">{{ errors.first('l_name') }}</small>-->
                            </div>
                        </div>
                        <div class="form-group py-1">
                            <input class="form-control border-bottom" name="c_email" v-model="newCustomerForm.c_email" type="email" placeholder="Email" />
                        </div>
                        <div class="form-group">
                            <input class="form-control border-bottom" v-model="newCustomerForm.phone_no" name="phone_no" type="text" placeholder="Phone Number" />
                        </div>

                     <div v-if="companySettings.enable_tims">
                        <div  class="form-group py-1" >

                            <input class="form-control border-bottom" name="krapin" v-model="newCustomerForm.krapin" type="text" placeholder="KRA PIN"/>
                        </div>
                        <div  class="form-group py-1" >

                            <input class="form-control border-bottom" name="company" v-model="newCustomerForm.company" type="company" placeholder="Name of Company"/>
                        </div>
                    </div>
                        <div class="form-row py-1">
                            <div class="col-6">
                                <input class="form-control border-bottom" v-model="newCustomerForm.h_no" name="h_no" type="text" placeholder="House Number" />
                            </div>
                            <div class="col-6">
                                <input class="form-control border-bottom" v-model="newCustomerForm.apartment" name="apartment" type="text" placeholder="Apartment" />
                            </div>
                        </div>
                        <div class="form-row py-1">
                            <div class="col-6">
                                <input class="form-control border-bottom" v-model="newCustomerForm.street" name="street" type="text" placeholder="Street" />
                            </div>
                            <div class="col-6">
                                <input class="form-control border-bottom" v-model="newCustomerForm.town" name="town" type="text" placeholder="Town" />
                            </div>
                        </div>
                        <div class="form-group pt-3">
                            <button class="btn btn-primary btn-block">Add Customer</button>
                        </div>
                    </form>
                </div>
            </b-modal>
            <b-modal ref="selected_bnpl_customer_modal" hide-footer>
                <div>
                    <p>Type to start searching customer</p>
                    <div class="input-group">
                        <input v-model=" customerData.searchTerm" type="text" class="form-control" placeholder="Search by name, email, or phone number">
                    </div>
                    <ul class="my-3" v-if="customerData.allcustomers.length > 0">
                        <li v-for="customer in customerData.allcustomers" :key="customer.id" @click="selectBNPLCustomer(customer)">
                            {{ customer.full_name }} - {{ customer.phone }}
                            <hr class="m-0 my-1" />
                        </li>
                    </ul>
                </div>
            </b-modal>
            <b-modal ref="bnpl_modal" hide-footer @show="prefillBnplDueDate">
                <div class="text-center">
                    <h5>Buy Now Pay Later Details</h5>
                </div>
                <small v-if="bnplForm.customerError !==''" class="text-danger">{{ bnplForm.customerError }}</small>
                <form @submit.prevent="processBNPLPayment()">
                    <div class="form-group">
                        <label>Customer Details</label>
                        <input v-if="bnplForm?.orderDetails?.customer !==null" type="text" class="form-control" v-model="bnplForm?.orderDetails?.customer.full_name" placeholder="Customer Name/Phone/Email" readonly/>
                        <p v-if="bnplForm?.orderDetails?.customer ===null"><a href="#" @click="selectedBNPLCustomerModal">Select Customer</a></p>
                        <p v-if="bnplForm?.orderDetails?.customer !==null"><a href="#" @click="selectedBNPLCustomerModal">Select To Change Customer</a></p>
                    </div>
                    <div class="form-group">
                        <label>Total Amount</label>
                        <input type="integer" class="form-control" v-model="bnplForm.orderDetails.balance" readonly/>
                    </div>
                    <div class="form-group">
                        <label>Deposit</label>
                        <input type="integer" class="form-control" v-model="bnplForm.depositAmount" />
                    </div>
                    <div class="form-group" v-if="Number(bnplForm.depositAmount) > 0">
                        <label>Deposit Payment Method</label>
                        <select class="custom-select" v-model="bnplForm.depositPaymentMethod">
                            <option :value="null">--Select Payment--</option>
                            <template v-for="payment in paymentGateways">
                                <option
                                    v-if="payment.enabled && payment.payment_method.toLowerCase() !== 'buy now pay later'"
                                    :key="payment.id"
                                    :value="payment"
                                >{{ payment.payment_method + (payment.account_label ? ' · ' + payment.account_label : payment.account_number ? ' · ' + payment.account_number : '') }}</option>
                            </template>
                        </select>
                        <small v-if="bnplForm.depositPaymentMethodError" class="text-danger">{{ bnplForm.depositPaymentMethodError }}</small>
                    </div>
                    <div class="form-group">
                        <label>Due Date</label>
                        <date-range-picker v-model="bnplDuePicker" :single-date-picker="true" :auto-apply="true" :ranges="false" :append-to-body="true" :min-date="bnplForm.minimumDate" class="w-100">
                            <template #input="picker">
                                <span v-if="picker.startDate">{{ formatBnplDue(picker.startDate) }}</span>
                                <span v-else class="text-secondary">Pick the due date</span>
                            </template>
                        </date-range-picker>
                    </div>
                    <div class="form-group">
                        <button type="submit" class="btn btn-outline-primary btn-block">Process BNPL</button>
                    </div>
                </form>
            </b-modal>
            <b-modal ref="errorModal" hide-footer>
                <div class="container text-center">
                    <h6 class="text-danger">
                        There was an error
                    </h6>
                    <hr/>
                    <p>{{ errorModalMessage }}</p>
                    <a class="btn btn-outline-primary my-2 px-1" href="/makesale">Make a new sale</a>
                </div>
            </b-modal>
            <b-modal id="create-kitchen-note-modal" hide-footer hid-header>
                <h5 class="py-2">Add a note to kitchen</h5>
                <form @submit.prevent="sendOrderToKitchen">
                    <div class="form-group">
                        <textarea class="form-control" id="exampleFormControlTextarea1" v-model="noteForm.noteText" rows="3" placeholder="Enter your note..."></textarea>
                    </div>
                    <div class="custom-control custom-switch">
                        <input type="checkbox" v-model="noteForm.addToReceipt" class="custom-control-input" id="customSwitch1">
                        <label class="custom-control-label" for="customSwitch1">Add to Receipt</label>
                    </div>
                    <div class="d-inline  ">
                        <div class="d-flex" v-if="companySettings.has_kds">
                            <button type="submit" class="btn btn-primary rounded flex-grow-1 mx-1 ">Save & Send to Kitchen</button>
                            <button @click="noteForm.printOnly = true" type="submit" class="btn btn-outline-primary rounded flex-grow-1 ">Skip and Print Order</button>
                        </div>
                    </div>
                </form>
            </b-modal>

        </div>
    </div>
</template>

<script>
    import moment from 'moment';
import DateRangePicker from 'vue2-daterange-picker/src/components/DateRangePicker';
import { mapGetters } from 'vuex';
    import PaymentValidationWaiting from './partials/PaymentValidationWaiting.vue';
    import MpesaStkModal from './partials/MpesaStkModal.vue';
    import CustomerSearchSelect from './partials/CustomerSearchSelect.vue';

export default {
  name: "checkout",

  components: { PaymentValidationWaiting, CustomerSearchSelect, MpesaStkModal, DateRangePicker },

  props: {
    orderid: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const minDate = new Date(today);
    minDate.setMonth(minDate.getMonth() - 2);
    minDate.setDate(15);

    return {
      loading: true,
      orderDetails: {},
      errorModalMessage: '',
      storeSettings: {},
      splitPaymentSelect: null,
      splitPaymentSelectError: '',
      splitPaymentAmount: false,
      paymentGateways: [],
      disabledPaymentGateways: 0,
      paidAmount: 0,
      orderamount: '',
      changeToCustomer: 0,
      isSubmitting: false,
      companySettings: {},

      // PERF: Optimized customer search
      customerData: {
        searchTerm: '',
        allcustomers: []
      },
      searchTimeout: null,

      // Kitchen note
      noteForm: {
        addToReceipt: false,
        noteText: '',
        printOnly: false,
      },

      // BNPL
      bnplForm: {
        dueDate: '',
        depositAmount: 0.00,
        interest: false,
        minimumDate: minDate,
        paymentId: '',
        depositPaymentMethod: null,
        depositPaymentMethodError: '',
        customerError: '',
        orderDetails: {
          customer: { full_name: '' }
        }
      },

      // New customer form
      newCustomerForm: {
        town: '',
        street: '',
        apartment: '',
        h_no: '',
        phone_no: '',
        c_email: '',
        l_name: '',
        f_name: '',
        krapin: '',
        company:'',
      },

      walletBalance: null,
      walletPaymentError: '',

      loyaltyBalance: null,
      loyaltyLoading: false,
      showLoyaltyRedeemInput: false,
      redeemPointsInput: null,
      redeemingLoyalty: false,
      loyaltyRedeemError: '',
      loyaltyRedeemedPoints: null,
      loyaltyOtpSentTo: '',
      loyaltyOtpInput: '',

      excessInfo: null,
      excessWalletCustomerId: null,
      excessWalletCustomerLabel: '',
      showExcessCustomerSearch: false,
      excessResolving: false,
      excessError: '',

      cashPaymentData: null,
      cashAmountError: '',
      checkOutDoneLink: '',
      splitPaymentBtnTxt: 'Process Payment',
      mpesaPromptPaymentId: null,
      mpesaPromptAmount: null,
      selectedPaymentMethod: '',
      checkmpesaresponsepoll: null,
      validationActive: false,
      validationAmount: 0,
      validationPaymentMethod: '',
      validationPaymentId: '',

      dukakitChoicePayment: null,
      dukakitChoiceAmount: 0,
      dukakitPhoneNumber: '',
      dukakitPhoneValidMessage: '',
      dukakitPushSending: false,
      dukakitPushError: '',
      dukakitPaymentId: null,
      dukakitPollInterval: null,
      dukakitStatusHolder: '<div><h4>Validating payment</h4></div>' +
        '<div class="spinner-border mt-5 text-primary" role="status">' +
        '<span class="sr-only">Loading...</span></div>' +
        '<p class="py-2">Please ask customer to check their phone and enter their M-Pesa PIN</p>' +
        '<p class="py-2">Awaiting confirmation....</p>'
    };
  },

  computed: {
    // BNPL due date via the app's standard date picker; bnplForm.dueDate stays 'YYYY-MM-DD'.
    bnplDuePicker: {
      get() { return { startDate: this.bnplForm.dueDate ? new Date(this.bnplForm.dueDate) : null, endDate: this.bnplForm.dueDate ? new Date(this.bnplForm.dueDate) : null } },
      set(val) { this.bnplForm.dueDate = val && val.startDate ? moment(val.startDate).format('YYYY-MM-DD') : '' },
    },
    ...mapGetters(['userDetails']),

    // PERF: Cached computed properties to avoid recalculation
    hasCustomer() {
      return this.orderDetails.customer && this.orderDetails.customer !== null;
    },

    customerFullName() {
      return this.hasCustomer && this.orderDetails.customer.full_name || '';
    },

    customerPhone() {
      return this.hasCustomer && this.orderDetails.customer.phone || '';
    },

    customerEmail() {
      return this.hasCustomer && this.orderDetails.customer.email || '';
    },

    customerAddress() {
      if (!this.hasCustomer) return '';
      const apt = this.orderDetails.customer.apartment || '';
      const addr = this.orderDetails.customer.address_address || '';
      return `${apt} ${addr} Kenya`.trim();
    },

    displayAmount() {
      // A loyalty redemption reduces `balance` without setting `partial`
      // (it isn't a payment) — show the discounted balance in that case
      // too, not just when a partial payment has already happened.
      if ((this.orderDetails.partial || this.orderDetails.loyalty_discount > 0) && this.orderDetails.balance !== undefined) {
        return this.convertCurrencyToLocal(this.orderDetails.balance);
      }
      return this.convertCurrencyToLocal(this.orderDetails.total_amount || 0);
    },

    hasKDS() {
      return this.companySettings && this.companySettings.has_kds === true;
    },

    canProcessPayments() {
      if (!this.userDetails || !this.userDetails.roles) return false;
      const isAdmin = this.userDetails.roles[0] && this.userDetails.roles[0].name === 'Admin';
      return isAdmin || this.hasPermission('can manage orders');
    },

    canDeleteOrders() {
      if (!this.userDetails || !this.userDetails.roles) return false;
      const isAdmin = this.userDetails.roles[0] && this.userDetails.roles[0].name === 'Admin';
      const canDelete = this.companySettings && this.companySettings.allow_employees_delete_orders;
      return isAdmin || canDelete;
    },

    printInvoiceUrl() {
      return this.orderDetails && this.orderDetails.id
        ? `/print_invoice/${this.orderDetails.id}`
        : '#';
    },

    // PERF: Memoized order items
    validOrderItems() {
      if (!this.orderDetails.orders || !Array.isArray(this.orderDetails.orders)) {
        return [];
      }
      return this.orderDetails.orders.filter(item => item.product || item.is_grouped);
    },

    formattedSubTotal() {
      return this.convertCurrencyToLocal(this.orderDetails.total_amount || 0);
    },

    formattedDiscount() {
      return this.convertCurrencyToLocal(this.orderDetails.discount || 0);
    },

    formattedBalance() {
      return this.convertCurrencyToLocal(this.orderDetails.balance || 0);
    },

    formattedGrandTotal() {
      return this.convertCurrencyToLocal(this.orderDetails.total_amount || 0);
    },

    hasDiscount() {
      return this.orderDetails.discount !== null && this.orderDetails.discount !== undefined;
    },

    isPartialPayment() {
      return this.orderDetails.partial === 1;
    },

    hasDisabledPayments() {
      return this.disabledPaymentGateways > 0;
    },

    paymentValidationEnabled() {
      return !!this.companySettings?.enable_payment_validation;
    },

    walletEnabled() {
      return !!(this.storeSettings?.enable_customer_wallet || this.companySettings?.enable_customer_wallet) && this.hasCustomer;
    },

    loyaltyEnabled() {
      return !!(this.storeSettings?.has_loyalty || this.companySettings?.has_loyalty);
    },

    appliedLoyaltyPoints() {
      return this.loyaltyRedeemedPoints || '';
    },
  },

  // PERF: Load all data in parallel on mount
  async mounted() {
    try {
      await Promise.all([
        this.getData(),
        this.getPaymentGateways(),
        this.getStoreSettings(),
        this.getCustomers()
      ]);
      if (this.walletEnabled && this.orderDetails?.customer?.id) {
        this.getWalletBalance(this.orderDetails.customer.id);
      }
      if (this.loyaltyEnabled && this.orderDetails?.customer?.id) {
        this.getLoyaltyBalance(this.orderDetails.customer.id);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      this.showError('Failed to load page data');
    }
  },

  // PERF: Critical cleanup to prevent memory leaks
  beforeDestroy() {
    this.stopMpesaPolling();
    this.stopDukakitPolling();

    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },

  watch: {
    paidAmount(newVal) {
      this.changeToCustomer = newVal - this.splitPaymentAmount;
    },

    splitPaymentSelect(newVal) {
      if (newVal && newVal.payment_method) {
        this.splitPaymentBtnTxt = `Process Payment - ${newVal.payment_method}`;
      }
    },

    // PERF: Debounced customer search
    'customerData.searchTerm'() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.getCustomers();
      }, 500);
    },

    userDetails: {
      immediate: true,
      handler(val) {
        if (val && val.company && val.company.companysettings) {
          this.companySettings = val.company.companysettings;
        }
      }
    }
  },

  methods: {
    formatBnplDue(d) { return moment(d).format('ddd D MMM YYYY') },
    // Default due date from BNPL settings ("default time to pay"); only when
    // the cashier hasn't picked one.
    async prefillBnplDueDate() {
      if (this.bnplForm.dueDate) return
      try {
        const { data } = await axios.get('/api/bnpl/terms')
        if (!this.bnplForm.dueDate && data.default_due_date) this.bnplForm.dueDate = data.default_due_date
      } catch (e) { /* keep the manual date picker */ }
    },

    // Helper methods - PERF: Inline calculations where possible
    getProductName(orderItem) {
      return orderItem.product?.product || 'Unknown Product';
    },

    getGroupTitle(orderItem) {
      return orderItem.grouped_inventory?.group_title || 'Grouped Item';
    },

    getGroupedItems(orderItem) {
      if (!orderItem.grouped_inventory?.grouped_items) return [];
      return orderItem.grouped_inventory.grouped_items.filter(item =>
        item.product && item.product.can_be_sold
      );
    },

    getItemDisplay(item) {
      const productName = item.product?.product || 'Item';
      const count = item.count || 0;
      return `${productName} - (${count})`;
    },

    // PERF: Optimized amount calculation
    calculateItemAmount(orderItem) {
      if (orderItem.product_id) {
        const price = orderItem.is_retail === 1
          ? (orderItem.product?.ppu || 0)
          : (orderItem.product?.wholesale_sp || 0);
        return price * orderItem.qty;
      } else if (orderItem.is_grouped) {
        const price = orderItem.is_retail === 1
          ? (orderItem.grouped_inventory?.cost || 0)
          : (orderItem.grouped_inventory?.wholesale || 0);
        return price * orderItem.qty;
      }
      return 0;
    },

    hasPermission(permissionName) {
      if (!this.userDetails?.permissions) return false;
      return (this.userDetails?.permissions || []).some(p => p.name === permissionName);
    },

    // Kitchen operations
    async sendOrderToKitchen() {
      try {
        const path = window.location.pathname;
        const id = path.split('/').pop();
        const res = await axios.post(`/api/kds/send-to-kitchen/${id}`, this.noteForm);
        const kitchenOrders = res.data.data || [];
        if (res.status === 200 && kitchenOrders.length && kitchenOrders[0].printed_at !== null) {
            // One ticket per station — pass every kitchen order, not just the first
            const ids = kitchenOrders.map(k => k.id).join(',');
            window.location.href = `/print_invoice/${kitchenOrders[0].final_order_id}?order-receipt=true&kitchen-order=${ids}`
        }
        else{

            window.location.href = '/orders';
        }
      } catch (error) {
        console.log(error)
        this.showError('There was an error sending to kitchen');
      }
    },

    // Modal controls
    selectedBNPLCustomerModal() {
      this.$refs.selected_bnpl_customer_modal?.show();
    },

    selectBNPLCustomer(customer) {
      this.bnplForm.orderDetails.customer = customer;
      this.$refs.selected_bnpl_customer_modal?.hide();
    },

    choosePaymentMethodModalShow() {
      const balance = parseInt(this.orderDetails.balance || 0);
      const totalAmount = this.orderDetails.total_amount || 0;
      const discount = this.orderDetails.discount || 0;

      if ((balance >= 0 || totalAmount === discount) && this.paymentGateways) {
        this.splitPaymentAmount = this.orderDetails.balance;
        this.$refs.choosePaymentMethodModal?.show();
      }
    },

    orderGoBack() {
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('source');
      if (source) {
        window.location.href = source;
      } else {
        this.discardOrder(this.orderDetails);
      }
    },

    // BNPL Payment
    async processBNPLPayment() {
      if (this.bnplForm.orderDetails.customer === null) {
        this.bnplForm.customerError = 'You need to select a customer';
        return;
      }

      this.bnplForm.depositPaymentMethodError = '';
      if (Number(this.bnplForm.depositAmount) > 0 && !this.bnplForm.depositPaymentMethod) {
        this.bnplForm.depositPaymentMethodError = 'Select how the deposit was paid';
        return;
      }

      try {
        const response = await axios.post('/api/pay-via-bnpl', {
          ...this.bnplForm,
          depositPaymentMethod: this.bnplForm.depositPaymentMethod?.id ?? null,
        });
        if (response.status === 200) {
          this.checkOutDoneLink = this.storeSettings.skip_checkout_done
            ? `/print_invoice/${response.data.final_order_id}`
            : `/paid/view-order-details/${response.data.final_order_id}`;
          this.$store.commit('resetStore');
          window.location.replace(this.checkOutDoneLink);
        }
      } catch (error) {
        this.$refs.bnpl_modal?.hide();
        this.showError('This order already has BNPL payment, you can set it only once!');
      }
    },

    // Customer management
    async newCustomerInfo() {
      try {
        const response = await axios.post('/api/newcustomer', ...this.newCustomerForm);
        if (response.status === 200) {
          this.bnplForm.orderDetails.customer = response.data.customer;
          this.showSuccess('Customer added successfully');
        }
      } catch (error) {
        this.showError('Failed to add customer');
      }
    },

    async getCustomers() {
      try {
        const response = await axios.get(`/api/allcustomers?search=${this.customerData.searchTerm}`);
        if (response.status === 200) {
          this.customerData.allcustomers = Array.isArray(response.data.data)
            ? response.data.data
            : [];
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
      }
    },

    // PERF: Parallel data loading
    async getData() {
      try {
        const response = await axios.get('/api/order-details-minimal', {
          params: { orderId: this.orderid }
        });
        this.orderDetails = response.data;
      } catch (error) {
        this.showError('Failed to load order details');
      } finally {
        this.loading = false;
      }
    },

    async getWalletBalance(customerId) {
      try {
        const { data } = await axios.get(`/api/subscriptions/wallet/${customerId}`);
        this.walletBalance = data.balance;
      } catch (e) {
        this.walletBalance = null;
      }
    },

    async getLoyaltyBalance(customerId) {
      this.loyaltyLoading = true;
      try {
        const { data } = await axios.get(`/api/loyalty/members/${customerId}`);
        this.loyaltyBalance = data.balance;
      } catch (e) {
        this.loyaltyBalance = null;
      } finally {
        this.loyaltyLoading = false;
      }
    },

    // Step 1: ask the server to text the customer a code. If the program
    // doesn't require one, redeem straight away.
    async redeemLoyaltyPoints() {
      this.loyaltyRedeemError = '';
      const points = parseInt(this.redeemPointsInput, 10);
      if (!points || points <= 0) {
        this.loyaltyRedeemError = 'Enter how many points to redeem.';
        return;
      }
      this.redeemingLoyalty = true;
      try {
        const { data } = await axios.post(`/api/loyalty/orders/${this.orderDetails.id}/redeem/otp`, { points });
        if (data.otp_required) {
          this.loyaltyOtpSentTo = data.masked_phone;
          this.loyaltyOtpInput = '';
          return;
        }
        await this.submitLoyaltyRedemption(points, null);
      } catch (e) {
        this.loyaltyRedeemError = e.response?.data?.message || 'Failed to redeem points.';
      } finally {
        this.redeemingLoyalty = false;
      }
    },

    // Step 2: the cashier enters the code the customer read out.
    async confirmLoyaltyOtp() {
      this.loyaltyRedeemError = '';
      this.redeemingLoyalty = true;
      try {
        await this.submitLoyaltyRedemption(parseInt(this.redeemPointsInput, 10), this.loyaltyOtpInput);
      } catch (e) {
        this.loyaltyRedeemError = e.response?.data?.message || 'Failed to redeem points.';
      } finally {
        this.redeemingLoyalty = false;
      }
    },

    cancelLoyaltyOtp() {
      this.loyaltyOtpSentTo = '';
      this.loyaltyOtpInput = '';
      this.loyaltyRedeemError = '';
      this.showLoyaltyRedeemInput = false;
    },

    async submitLoyaltyRedemption(points, otp) {
      const { data } = await axios.post(`/api/loyalty/orders/${this.orderDetails.id}/redeem`, { points, otp });
      this.orderDetails.balance = data.order.balance;
      this.orderDetails.loyalty_discount = data.order.loyalty_discount;
      this.loyaltyRedeemedPoints = points;
      this.loyaltyBalance = this.loyaltyBalance - points;
      this.redeemPointsInput = null;
      this.loyaltyOtpSentTo = '';
      this.loyaltyOtpInput = '';
      // Input collapses back to the "Customer's points · Redeem" link
      // (now hidden behind the "already redeemed" state above it).
      this.showLoyaltyRedeemInput = false;
      // The payment-method amount was pre-filled from the pre-redemption
      // balance when the modal opened — keep it in sync with the new,
      // reduced balance so the cashier isn't shown a stale amount.
      if (this.splitPaymentAmount !== false && this.splitPaymentAmount !== null) {
        this.splitPaymentAmount = this.orderDetails.balance;
      }
      this.$root.$toasts?.push({ type: 'success', message: `Redeemed ${points} points.` });
    },

    async undoLoyaltyRedemption() {
      this.redeemingLoyalty = true;
      try {
        const { data } = await axios.delete(`/api/loyalty/orders/${this.orderDetails.id}/redeem`);
        this.orderDetails.balance = data.order.balance;
        this.orderDetails.loyalty_discount = data.order.loyalty_discount;
        if (this.loyaltyRedeemedPoints) {
          this.loyaltyBalance = this.loyaltyBalance + this.loyaltyRedeemedPoints;
        }
        this.loyaltyRedeemedPoints = null;
        if (this.splitPaymentAmount !== false && this.splitPaymentAmount !== null) {
          this.splitPaymentAmount = this.orderDetails.balance;
        }
        this.$root.$toasts?.push({ type: 'success', message: 'Redemption undone.' });
      } catch (e) {
        this.$root.$toasts?.push({ type: 'error', message: e.response?.data?.message || 'Failed to undo redemption.' });
      } finally {
        this.redeemingLoyalty = false;
      }
    },

    async payWithWallet(amount) {
      this.walletPaymentError = '';
      const amt = parseFloat(amount ?? this.splitPaymentAmount);
      if (!amt || amt <= 0) {
        this.walletPaymentError = 'Enter a valid amount.';
        return;
      }
      this.isSubmitting = true;
      try {
        const { data } = await axios.post(`/api/orders/${this.orderDetails.id}/payment/wallet`, { amount: amt });
        this.orderDetails.balance = data.balance;
        this.walletBalance        = data.wallet_balance;
        this.$refs.choosePaymentMethodModal?.hide();
        if (data.balance <= 0) {
          this.checkOutDoneLink = this.storeSettings.skip_checkout_done
            ? `/print_invoice/${this.orderDetails.id}`
            : `/paid/view-order-details/${this.orderDetails.id}`;
          window.location.replace(this.checkOutDoneLink);
        } else {
          this.splitPaymentBtnTxt = 'Process the balance';
          this.splitPaymentSelect = null;
          this.splitPaymentAmount = this.orderDetails.balance;
          this.isSubmitting = false;
          this.choosePaymentMethodModalShow();
        }
      } catch (e) {
        this.walletPaymentError = e.response?.data?.message || 'Wallet payment failed.';
        this.isSubmitting = false;
      }
    },

    async getStoreSettings() {
      try {
        const response = await axios.get('/api/settings');
        this.storeSettings = response.data;
      } catch (error) {
        this.showError('Failed to load store settings');
      }
    },

    async getPaymentGateways() {
      try {
        const response = await axios.get('/api/payments');
        this.paymentGateways = Array.isArray(response.data) ? response.data : [];
        this.disabledPaymentGateways = this.paymentGateways.filter(p => !p.enabled).length;
      } catch (error) {
        this.showError('Failed to load payment gateways');
      }
    },

    // PERF: Streamlined split payment processing
    async processSplitPaymentModal() {
      this.$store.commit('resetStore');

      if (this.splitPaymentSelect === null) {
        this.splitPaymentSelectError = 'Select at least one mode of payment';
        return;
      }

      const balance = this.orderDetails.balance || 0;
      if (this.splitPaymentAmount < 0 || this.splitPaymentAmount > balance) {
        this.splitPaymentSelectError = `Please check the amount, the balance for this order is ${this.convertCurrencyToLocal(balance)}`;
        return;
      }

      this.splitPaymentBtnTxt = "Processing Payment...";
      this.splitPaymentSelectError = "";
      this.$refs.choosePaymentMethodModal?.hide();
      this.isSubmitting = true;

      await this.checkPaymentGateway(
        this.splitPaymentSelect,
        this.orderDetails,
        this.splitPaymentAmount
      );
    },

    async processSplitPayment() {
      if (!this.splitPaymentSelect?.id || !this.splitPaymentAmount || !this.orderDetails.id) {
        this.errorModalMessage = 'Please fill in all required fields: Payment Method, Amount, and Order ID.';
        this.$refs.errorModal?.show();
        this.isSubmitting = false;
        return;
      }

      try {
        const response = await axios.post('/api/split-payment', {
          paymentMethod: this.splitPaymentSelect.id,
          amount: this.splitPaymentAmount,
          orderId: this.orderDetails.id,
          ...(this.cashPaymentData ? { cash_received: this.cashPaymentData.received, change_given: this.cashPaymentData.change } : {})
        });

        if (response.status === 200) {
          this.checkOutDoneLink = this.storeSettings.skip_checkout_done
            ? `/print_invoice/${response.data.id}`
            : `/paid/view-order-details/${response.data.id}`;
          this.orderDetails = response.data;

          if (this.orderDetails.balance === 0) {
            this.cashPaymentData = null;
            window.location.replace(this.checkOutDoneLink);
          } else {
            this.splitPaymentBtnTxt = "Process the balance";
            this.splitPaymentSelect = null;
            this.splitPaymentAmount = this.orderDetails.balance;
            this.isSubmitting = false;
            this.choosePaymentMethodModalShow();
          }
        }
      } catch (error) {
        this.errorModalMessage = error.response?.data?.message || 'Error processing payment';
        this.$refs.errorModal?.show();
        this.isSubmitting = false;
        this.splitPaymentBtnTxt = "Retry Payment";
      }
    },

    async processFullPayment() {
      try {
        const response = await axios.post('/api/full-payment', {
          paymentMethod: this.splitPaymentSelect.id,
          amount: this.splitPaymentAmount,
          orderId: this.orderDetails.id,
          ...(this.cashPaymentData ? { cash_received: this.cashPaymentData.received, change_given: this.cashPaymentData.change } : {})
        });

        if (response.status === 200) {
          this.checkOutDoneLink = this.storeSettings.skip_checkout_done
            ? `/print_invoice/${response.data.id}`
            : `/paid/view-order-details/${response.data.id}`;
          this.orderDetails = response.data;

          if (this.orderDetails.balance === 0) {
            window.location.replace(this.checkOutDoneLink);
          }
        }
      } catch (error) {
        this.errorModalMessage = error.response?.data?.message || 'Error processing payment';
        this.$refs.errorModal?.show();
        this.isSubmitting = false;
        this.splitPaymentBtnTxt = "Retry Payment";
      }
    },

    cashCheckout() {
      const received = parseFloat(this.paidAmount) || 0;
      const due = parseFloat(this.splitPaymentAmount) || 0;
      if (received > 0 && received < due) {
        this.cashAmountError = `Cash received (${this.convertCurrencyToLocal(received)}) is less than amount due (${this.convertCurrencyToLocal(due)})`;
        return;
      }
      this.cashAmountError = '';
      this.cashPaymentData = received > 0
        ? { received, change: Math.max(0, received - due) }
        : null;
      if (this.splitPaymentAmount === this.orderDetails.total_amount) {
        this.processFullPayment();
      } else {
        this.processSplitPayment();
      }
      this.$refs.paidAmountmodal?.hide();
    },

    checkPaymentGateway(payment, orderDetails, amountToPay) {
      const link = this.storeSettings.skip_checkout_done
        ? `/print_invoice/${orderDetails.id}`
        : `/paid/view-order-details/${orderDetails.id}`;
      this.checkOutDoneLink = link;

      const paymentMethod = payment?.payment_method?.toLowerCase();
      this.orderamount = orderDetails.total_amount;

      if (!paymentMethod) {
        console.error("payment_method is missing:", payment);
        this.showError('Invalid payment method selected');
        return;
      }

      if (paymentMethod === 'wallet') {
        this.payWithWallet(amountToPay);
      } else if (paymentMethod === 'cash') {
        this.$refs.paidAmountmodal?.show();
        this.$store.commit('resetStore');
      } else if (paymentMethod === 'm-pesa') {
        if (this.storeSettings.use_dukakit_paybill) {
          this.selectedPaymentMethod = paymentMethod;
          // Remembered so a paid prompt can complete the order itself
          this.mpesaPromptPaymentId = payment.id;
          this.mpesaPromptAmount    = Number(amountToPay || orderDetails.balance || orderDetails.total_amount);
          this.$nextTick(() => this.$refs.mpesaStk?.open());
        } else if (this.paymentValidationEnabled) {
          this.validationActive        = true;
          this.validationAmount        = amountToPay || orderDetails.total_amount;
          this.validationPaymentMethod = paymentMethod;
          this.validationPaymentId     = payment.id;
          this.$refs.validationModal?.show();
        } else {
          if (amountToPay === orderDetails.total_amount) {
            this.processFullPayment();
          } else {
            this.processSplitPayment();
          }
          this.$store.commit('resetStore');
        }
      } else if (payment.kind === 'dukakit') {
        this.dukakitChoicePayment = payment;
        this.dukakitChoiceAmount = amountToPay || orderDetails.total_amount;
        if (this.paymentValidationEnabled) {
          // Straight to the matching-payment screen — it already offers a
          // "send prompt" fallback for phone numbers with nothing matched yet,
          // so there's no need for a separate "how is the customer paying?" step.
          this.validationActive        = true;
          this.validationAmount        = this.dukakitChoiceAmount;
          this.validationPaymentMethod = 'dukakit';
          this.validationPaymentId     = payment.id;
          this.$refs.validationModal?.show();
        } else {
          this.dukakitPushError = '';
          this.dukakitPhoneNumber = '';
          this.dukakitPhoneValidMessage = '';
          this.$refs['customer-dukakit-phone-request']?.show();
        }
      } else if (paymentMethod === 'buy now pay later') {
        this.bnplForm.paymentId = payment.id;
        this.bnplForm.depositPaymentMethod = null;
        this.bnplForm.depositPaymentMethodError = '';
        this.bnplForm.orderDetails = this.orderDetails;
        this.$refs.bnpl_modal?.show();
      } else {
        if (this.paymentValidationEnabled) {
          this.validationActive        = true;
          this.validationAmount        = amountToPay || orderDetails.total_amount;
          this.validationPaymentMethod = paymentMethod;
          this.validationPaymentId     = payment.id;
          this.$refs.validationModal?.show();
        } else {
          if (amountToPay === orderDetails.total_amount) {
            this.processFullPayment();
          } else {
            this.processSplitPayment();
          }
          this.$store.commit('resetStore');
        }
      }
    },

    // No prompt (e.g. own Paybill without API keys): carry on as M-Pesa did
    // before STK - SMS validation if it's on, otherwise record it directly.
    onStkSkip() {
      const amount = this.mpesaPromptAmount || this.orderDetails.balance;
      if (this.paymentValidationEnabled) {
        this.validationActive        = true;
        this.validationAmount        = amount;
        this.validationPaymentMethod = this.selectedPaymentMethod;
        this.validationPaymentId     = this.mpesaPromptPaymentId;
        this.$refs.validationModal?.show();
      } else {
        // Same as before STK: the chosen method/amount are already selected.
        if (Number(amount) === Number(this.orderDetails.total_amount)) {
          this.processFullPayment();
        } else {
          this.processSplitPayment();
        }
      }
    },

    async onStkPaid({ transactionsValidatedId }) {
      await this.completeMpesaPromptPayment(transactionsValidatedId);
    },

    async onValidated(validationResult) {
      this.validationActive = false;
      this.$refs.validationModal?.hide();
      try {
        const amount = validationResult?.applied_amount ?? this.validationAmount;
        const isPartialSplit = amount < this.orderDetails.balance;
        const endpoint = isPartialSplit ? '/api/split-payment' : '/api/full-payment';

        const response = await axios.post(endpoint, {
          paymentMethod:              this.validationPaymentId,
          amount:                     amount,
          orderId:                    this.orderDetails.id,
          transactions_validated_id:  validationResult?.transaction?.id || null,
        });

        if (response.status === 200) {
          this.orderDetails = response.data;
          this.checkOutDoneLink = this.storeSettings.skip_checkout_done
            ? `/print_invoice/${this.orderDetails.id}`
            : `/paid/view-order-details/${this.orderDetails.id}`;

          if (this.orderDetails.balance === 0) {
            window.location.replace(this.checkOutDoneLink);
          } else {
            this.splitPaymentBtnTxt = "Process the balance";
            this.splitPaymentSelect = null;
            this.splitPaymentAmount = this.orderDetails.balance;
            this.isSubmitting = false;
            this.choosePaymentMethodModalShow();
          }
        }
      } catch (error) {
        this.errorModalMessage = error.response?.data?.message || 'Payment processing failed after validation';
        this.$refs.errorModal?.show();
      }
    },

    onExcessDetected(payload) {
      this.validationActive = false;
      this.$refs.validationModal?.hide();

      this.excessInfo = payload;
      this.excessError = '';
      this.excessResolving = false;

      if (this.orderDetails.customer) {
        this.excessWalletCustomerId = this.orderDetails.customer.id;
        this.excessWalletCustomerLabel = this.orderDetails.customer.full_name || '';
        this.showExcessCustomerSearch = false;
      } else {
        this.excessWalletCustomerId = null;
        this.excessWalletCustomerLabel = '';
        this.showExcessCustomerSearch = true;
      }

      this.$refs.excessWalletModal?.show();
    },

    onExcessWalletCustomerSelected(customer) {
      this.excessWalletCustomerLabel = customer ? customer.full_name : '';
    },

    async resolveExcessToWallet() {
      if (!this.excessWalletCustomerId || !this.excessInfo) return;

      this.excessResolving = true;
      this.excessError = '';
      try {
        const payResponse = await axios.post('/api/full-payment', {
          paymentMethod: this.validationPaymentId,
          amount:        this.excessInfo.order_balance,
          orderId:       this.orderDetails.id,
        });

        await axios.post('/api/payment-validation/confirm-order-reference-with-wallet', {
          transactions_validated_id: this.excessInfo.transactions_validated_id,
          order_id:                  this.orderDetails.id,
          wallet_customer_id:        this.excessWalletCustomerId,
          excess_amount:             this.excessInfo.excess,
        });

        this.orderDetails = payResponse.data;
        this.excessInfo = null;
        this.$refs.excessWalletModal?.hide();

        this.checkOutDoneLink = this.storeSettings.skip_checkout_done
          ? `/print_invoice/${this.orderDetails.id}`
          : `/paid/view-order-details/${this.orderDetails.id}`;
        window.location.replace(this.checkOutDoneLink);
      } catch (error) {
        this.excessError = error.response?.data?.message || 'Failed to complete payment.';
      } finally {
        this.excessResolving = false;
      }
    },

    async discardOrder(orderDetails) {
      if (!orderDetails?.id) {
        this.showError('Cannot cancel order: Invalid order details');
        return;
      }

      try {
        const response = await axios.delete(`/api/delete-order/${orderDetails.id}`);
        if (response.status === 200) {
          this.$store.commit('resetStore');
          window.location.replace('/makesale');
        }
      } catch (error) {
        this.showError('Failed to cancel order');
      }
    },

    // The customer paid the prompt: record it against the order exactly like
    // a validated M-Pesa payment (onValidated), then go to the receipt - or
    // reopen the chooser if this was one leg of a split payment.
    async completeMpesaPromptPayment(transactionsValidatedId) {
      const amount = this.mpesaPromptAmount || this.orderDetails.balance;
      const isPartialSplit = amount < this.orderDetails.balance;

      // Tie the M-Pesa transaction to the order first, as SMS validation
      // does, so it shows as Validated on Payment Transactions and the
      // ledger. A failure here must not block recording the paid order.
      if (transactionsValidatedId) {
        try {
          await axios.post('/api/payment-validation/validate-by-transaction', {
            transactions_validated_id: transactionsValidatedId,
            record_type: 'order',
            record_id: this.orderDetails.id,
          });
        } catch (error) {
          console.error('Could not link M-Pesa transaction to order:', error);
        }
      }

      try {
        const response = await axios.post(isPartialSplit ? '/api/split-payment' : '/api/full-payment', {
          paymentMethod: this.mpesaPromptPaymentId,
          amount: amount,
          orderId: this.orderDetails.id,
          transactions_validated_id: transactionsValidatedId || null,
        });
        this.orderDetails = response.data;
      } catch (error) {
        this.$refs.mpesaStk?.failCompletion(error.response?.data?.message || 'M-Pesa payment was received but the order could not be completed. Record it manually.');
        return;
      }
      this.$refs.mpesaStk?.done();

      setTimeout(() => {

        if (Number(this.orderDetails.balance) > 0) {
          this.orderDetails.partial = true;
          this.splitPaymentBtnTxt = 'Process the balance';
          this.splitPaymentSelect = null;
          this.splitPaymentAmount = this.orderDetails.balance;
          this.isSubmitting = false;
          this.choosePaymentMethodModalShow();
          return;
        }

        this.$store.commit('resetStore');
        window.location.replace(this.storeSettings.skip_checkout_done
          ? `/print_invoice/${this.orderDetails.id}`
          : `/paid/view-order-details/${this.orderDetails.id}`);
      }, 2000);
    },

    stopMpesaPolling() {
      if (this.checkmpesaresponsepoll) {
        clearInterval(this.checkmpesaresponsepoll);
        this.checkmpesaresponsepoll = null;
      }
    },

    validateDukakitPhoneNumber(phoneNumber) {
      this.dukakitPhoneValidMessage = phoneNumber.valid ? '' : 'Enter a valid phone number';
    },

    // Fired by PaymentValidationWaiting's "Send payment prompt" fallback -
    // reached when the cashier typed a phone number into the unified
    // matching-payment input and nothing has matched yet.
    async onDukakitPushRequested(payload) {
      this.dukakitPhoneNumber = payload.phone;
      this.dukakitPhoneValidMessage = '';
      this.dukakitPushError = '';
      await this.sendDukakitStkPush({ fromValidation: true });
    },

    async sendDukakitStkPush({ fromValidation = false } = {}) {
      if (!this.dukakitPhoneNumber || this.dukakitPhoneValidMessage) return;

      this.dukakitPushSending = true;
      this.dukakitPushError = '';

      try {
        const response = await axios.post(`/api/dukakit-payments/checkout/orders/${this.orderDetails.id}/stk`, {
          phone_number: this.dukakitPhoneNumber,
          // splitPaymentAmount is the amount for THIS payment leg - the
          // same variable every other payment method (cash, M-Pesa) reads
          // when posting to /api/split-payment or /api/full-payment. It's
          // pre-filled to the full balance by choosePaymentMethodModalShow()
          // and only differs when the cashier edited it for a split payment.
          amount: this.splitPaymentAmount,
        });

        this.dukakitPaymentId = response.data.payment_id;
        this.$refs['customer-dukakit-phone-request']?.hide();
        // Keep the validation modal open on failure (its push-prompt button
        // surfaces dukakitPushError and lets the cashier retry) - only swap
        // over to the polling modal once the push actually goes out.
        if (fromValidation) {
          this.validationActive = false;
          this.$refs.validationModal?.hide();
        }
        this.$refs.process_dukakit?.show();
        this.startDukakitPolling();
      } catch (error) {
        this.dukakitPushError = error.response?.data?.message || `Could not send the ${this.$platformBrand.name} payment request. Please try again.`;
      } finally {
        this.dukakitPushSending = false;
      }
    },

    startDukakitPolling() {
      this.stopDukakitPolling();

      this.dukakitPollInterval = setInterval(async () => {
        try {
          const response = await axios.get(`/api/dukakit-payments/payments/${this.dukakitPaymentId}/status`);
          const status = response.data.status;

          if (status === 'completed') {
            this.dukakitStatusHolder = '<div class="success-checkmark">' +
              '<div class="check-icon">' +
              '<span class="icon-line line-tip"></span>' +
              '<span class="icon-line line-long"></span>' +
              '<div class="icon-circle"></div>' +
              '<div class="icon-fix"></div>' +
              '</div></div>' +
              '<div class="text-center">' +
              '<h6 class="text-smanel-gray-lvl3">Your payment has been received and your account has been credited</h6>' +
              '</div>';

            const remainingBalance = Number(response.data.order_balance ?? 0);

            setTimeout(() => {
              this.stopDukakitPolling();
              this.$refs.process_dukakit?.hide();

              if (remainingBalance <= 0) {
                window.location.replace(this.checkOutDoneLink);
                this.$store.commit('resetStore');
              } else {
                // Split payment leg - mirrors processSplitPayment()'s
                // "still has a balance" branch: reopen the payment-method
                // chooser pre-filled with what's left, instead of redirecting.
                this.orderDetails.balance = remainingBalance;
                this.orderDetails.partial = true;
                this.splitPaymentBtnTxt = 'Process the balance';
                this.splitPaymentSelect = null;
                this.splitPaymentAmount = remainingBalance;
                this.isSubmitting = false;
                this.choosePaymentMethodModalShow();
              }
            }, 3000);
          }

          if (status === 'failed') {
            const reason = response.data.result_desc || 'There was an issue with the payment.';
            this.dukakitStatusHolder = '<div class="container text-center">' +
              '<h5 class="text-danger text-smanel-50">' +
              '<i class="bi bi-x-circle"></i></h5>' +
              `<p>${reason}</p>` +
              `<a class="btn btn-primary" href="/checkout/${this.orderDetails.order}">Retry Payment</a>` +
              '</div>';
            this.stopDukakitPolling();
          }
        } catch (error) {
          console.error('Error checking Dukakit payment status:', error);
        }
      }, 5000);
    },

    stopDukakitPolling() {
      if (this.dukakitPollInterval) {
        clearInterval(this.dukakitPollInterval);
        this.dukakitPollInterval = null;
      }
    },

    // Utility methods
    showError(message) {
      if (this.$toasts?.push) {
        this.$toasts.push({ type: 'error', message });
      } else {
        alert(message);
      }
    },

    showSuccess(message) {
      if (this.$toasts?.push) {
        this.$toasts.push({ type: 'success', message });
      }
    },

    convertCurrencyToLocal(amount) {
      if (this.$root?.convertCurrencyToLocal) {
        return this.$root.convertCurrencyToLocal(amount);
      }
      return amount ? amount.toFixed(2) : '0.00';
    }
  }
};
</script>
<style scoped>
/* Add any component-specific styles here */
</style>
